import {
  MEISAI_DATE_SORT_GROUPS,
  MEISAI_REVIEW_SORT_KEYS,
} from './detailGridTypes'

export type SortDirection = 'asc' | 'desc'

export interface SortSpec {
  key: string
  dir: SortDirection
}

const SHORT_FIELDS = new Set([
  'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10',
  'f19', 'f21', 'f22', 'f23', 'f24', 'f79',
])

const LONG_FIELDS = new Set([
  'f31', 'f32', 'f33', 'f34', 'f35', 'f36', 'f37', 'f38', 'f39', 'f40', 'f41',
  'f42', 'f43', 'f44', 'f45', 'f46', 'f47', 'f48', 'f49', 'f50', 'f51',
])

export interface DetailSortContext {
  detailRows: Record<string, string>[]
  detailReviewed?: boolean[]
  detailReviewMemos?: (string | null)[]
}

function sortKeysForColumn(key: string): readonly string[] {
  if (key in MEISAI_DATE_SORT_GROUPS) {
    return MEISAI_DATE_SORT_GROUPS[key]
  }
  if ((MEISAI_REVIEW_SORT_KEYS as readonly string[]).includes(key)) {
    return [key]
  }
  return [key]
}

/** sortSpec が参照する比較列 ID（日付複合キーは f2–f4 / f6–f8 全体）。sortSpec が null のときは空。 */
export function getSortParticipatingColumnIds(sortSpec: SortSpec | null): readonly string[] {
  if (!sortSpec) return []
  return sortKeysForColumn(sortSpec.key)
}

/** 指定 f 列が現在の並び替えに参与しているか。 */
export function isColumnUsedInSort(columnId: string, sortSpec: SortSpec | null): boolean {
  if (!sortSpec) return false
  return getSortParticipatingColumnIds(sortSpec).includes(columnId)
}

function cellValue(
  rowIdx: number,
  key: string,
  ctx: DetailSortContext,
): string {
  if (key === 'reviewed') {
    return ctx.detailReviewed?.[rowIdx] ? '1' : '0'
  }
  if (key === 'reviewMemo') {
    return ctx.detailReviewMemos?.[rowIdx] ?? ''
  }
  return ctx.detailRows[rowIdx]?.[key] ?? ''
}

function compareScalar(a: string, b: string, key: string): number {
  const aEmpty = a === ''
  const bEmpty = b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1

  if (key === 'reviewed') {
    return a.localeCompare(b, 'ja')
  }

  if (SHORT_FIELDS.has(key) || LONG_FIELDS.has(key)) {
    const aNum = Number(a)
    const bNum = Number(b)
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && /^-?\d+$/.test(a) && /^-?\d+$/.test(b)) {
      if (aNum < bNum) return -1
      if (aNum > bNum) return 1
      return 0
    }
  }

  return a.localeCompare(b, 'ja', { numeric: true })
}

/** 2 行を sortSpec に従って比較する。dir=desc の場合は呼び出し側で符号反転する。 */
export function compareDetailRows(
  rowA: number,
  rowB: number,
  sortSpec: SortSpec,
  ctx: DetailSortContext,
): number {
  const keys = sortKeysForColumn(sortSpec.key)
  for (const key of keys) {
    const cmp = compareScalar(cellValue(rowA, key, ctx), cellValue(rowB, key, ctx), key)
    if (cmp !== 0) {
      return sortSpec.dir === 'desc' ? -cmp : cmp
    }
  }
  return rowA - rowB
}

/** visibleRowIndices を sortSpec で並べ替えた display 用インデックスを返す。 */
export function sortRowIndices(
  indices: number[],
  sortSpec: SortSpec | null,
  ctx: DetailSortContext,
): number[] {
  if (!sortSpec) return indices
  return [...indices].sort((a, b) => compareDetailRows(a, b, sortSpec, ctx))
}

/** 三態ソート: null → asc → desc → null */
export function nextSortSpec(current: SortSpec | null, key: string): SortSpec | null {
  if (!current || current.key !== key) {
    return { key, dir: 'asc' }
  }
  if (current.dir === 'asc') {
    return { key, dir: 'desc' }
  }
  return null
}
